import { PRODUCT_NAME } from '../../content/provenance'

/**
 * PDF and Excel export for every report (docs/product/reports.md "Export").
 * A report hands over one table, the same rows its screen shows, and both
 * formats are written from it in the browser. The two writers are loaded on
 * first use so the report pages do not carry them.
 */
export type ExportCell = string | number | null

export type ExportColumn = {
  header: string
  numeric?: boolean
  // Keeps a numeric column against the left edge, such as a rank, in Excel
  // and in the PDF.
  leftAlign?: boolean
  // A whole-number percentage, shown with its % sign.
  percent?: boolean
  // Excel column width, in characters.
  width?: number
}

export type ExportTotals = {
  label: string
  // One entry per column, null where the column is not summed.
  values: (number | null)[]
  // Rows whose cell in this column equals the value are left out of the sum,
  // such as a row that restates the rows beneath it.
  excludeWhere?: { column: number; equals: string }
  // Columns whose total is the given value as is, because it is not a sum
  // of the rows above it, such as a percentage.
  fixedColumns?: number[]
}

// A second table above the main one: the totals a report states about the
// rows beneath it, with a total row of its own.
export type ExportSummary = {
  title: string
  columns: ExportColumn[]
  rows: ExportCell[][]
  total: ExportCell[]
}

export type ExportTable = {
  title: string
  parameters: { label: string; value: string }[]
  generatedAt: Date
  columns: ExportColumn[]
  rows: ExportCell[][]
  totals?: ExportTotals
  // A table too wide for a letter page: legal paper, smaller type.
  compact?: boolean
  summary?: ExportSummary
}

export function generatedLabel(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function parametersLine(table: ExportTable): string {
  return table.parameters.map((parameter) => `${parameter.label}: ${parameter.value}`).join('  |  ')
}

const PERCENT_FORMAT = '0"%"'

function columnLetter(index: number): string {
  let letter = ''
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) letter = String.fromCharCode(65 + ((n - 1) % 26)) + letter
  return letter
}

type SheetCell = null | string | number | { value: string | number; type?: typeof String | typeof Number | 'Formula'; fontWeight?: 'bold'; format?: string; align?: 'left' }

// Title row, parameters row, header row, then typed cells. A totals row uses
// formulas (written without the leading equals sign, as the file format
// stores them), so the sums recalculate when the sheet is edited.
export function excelSheetData(table: ExportTable, rowOffset = 0): SheetCell[][] {
  const bold = (value: string): SheetCell => ({ value, fontWeight: 'bold' })
  const data: SheetCell[][] = [
    [bold(`${PRODUCT_NAME} ${table.title}`)],
    [`${parametersLine(table)}  |  Generated: ${generatedLabel(table.generatedAt)}`],
    table.columns.map((column) => bold(column.header)),
    ...table.rows.map((row): SheetCell[] =>
      row.map((cell, index): SheetCell => {
        if (cell === null) return null
        const column = table.columns[index]
        if (!column.numeric) return String(cell)
        // A number format keeps the cell numeric, so it still sorts and sums.
        const align = column.leftAlign ? ({ align: 'left' } as const) : {}
        return column.percent ? { value: cell, type: Number, format: PERCENT_FORMAT, ...align } : { value: cell, type: Number, ...align }
      }),
    ),
  ]

  const totals = table.totals
  if (totals && table.rows.length > 0) {
    const first = 4 + rowOffset
    const last = 3 + rowOffset + table.rows.length
    data.push(
      totals.values.map((value, index): SheetCell => {
        if (index === 0) return bold(totals.label)
        if (value === null) return null
        if (totals.fixedColumns?.includes(index)) {
          return table.columns[index].percent
            ? { value, type: Number, fontWeight: 'bold', format: PERCENT_FORMAT }
            : { value, type: Number, fontWeight: 'bold' }
        }
        const range = `${columnLetter(index)}${first}:${columnLetter(index)}${last}`
        if (!totals.excludeWhere) return { value: `SUM(${range})`, type: 'Formula', fontWeight: 'bold' }
        const criteria = `${columnLetter(totals.excludeWhere.column)}${first}:${columnLetter(totals.excludeWhere.column)}${last}`
        return { value: `SUMIF(${criteria},"<>${totals.excludeWhere.equals}",${range})`, type: 'Formula', fontWeight: 'bold' }
      }),
    )
  }
  return data
}

// A table with a summary on one sheet, the way the PDF prints it: the title and
// parameters, the summary with its bold total row, a blank row, then the table.
export function summarySheetData(table: ExportTable): SheetCell[][] {
  const summary = table.summary!
  const bold = (value: string): SheetCell => ({ value, fontWeight: 'bold' })
  const typed = (row: ExportCell[], isTotal: boolean): SheetCell[] =>
    row.map((cell, index): SheetCell => {
      if (cell === null || cell === '') return null
      if (!summary.columns[index].numeric) return isTotal ? bold(String(cell)) : String(cell)
      return isTotal ? { value: cell, type: Number, fontWeight: 'bold' } : { value: cell, type: Number }
    })
  const head = [
    [bold(`${PRODUCT_NAME} ${table.title}`)],
    [`${parametersLine(table)}  |  Generated: ${generatedLabel(table.generatedAt)}`],
    [],
    [bold(summary.title)],
    summary.columns.map((column) => bold(column.header)),
    ...summary.rows.map((row) => typed(row, false)),
    typed(summary.total, true),
    [],
  ]
  // The table follows without repeating its title and parameters.
  return [...head, ...excelSheetData(table, head.length - 2).slice(2)]
}

const columnWidths = (columns: ExportColumn[]) => columns.map((column) => ({ width: column.width ?? (column.numeric ? 12 : 18) }))

// A table's cells and column widths as one sheet. With a summary the two share
// the sheet's columns, so each column takes the wider of the two widths.
function sheetOf(table: ExportTable) {
  if (!table.summary) return { data: excelSheetData(table), columns: columnWidths(table.columns) }
  const own = columnWidths(table.columns)
  const summaryWidths = columnWidths(table.summary.columns)
  const columns = Array.from({ length: Math.max(own.length, summaryWidths.length) }, (_, index) => ({
    width: Math.max(own[index]?.width ?? 0, summaryWidths[index]?.width ?? 0),
  }))
  return { data: summarySheetData(table), columns }
}

export async function exportExcel(table: ExportTable, fileName: string): Promise<void> {
  const { default: writeExcelFile } = await import('write-excel-file/browser')
  const { data, columns } = sheetOf(table)
  await writeExcelFile(data as never, { columns, sheet: table.title.slice(0, 31) } as never).toFile(fileName)
}

export type WorkbookSheet = { name: string; table: ExportTable }

// Every report as a tab of one workbook. A tab is named for its report, and
// the names are cut to the 31 characters a sheet name may have.
export async function exportWorkbook(sheets: WorkbookSheet[], fileName: string): Promise<void> {
  const { default: writeExcelFile } = await import('write-excel-file/browser')
  await writeExcelFile(
    sheets.map(({ name, table }) => ({ ...sheetOf(table), sheet: name.slice(0, 31) })) as never,
  ).toFile(fileName)
}

export type Wordmark = { dataUrl: string; width: number; height: number }

// The wordmark is an SVG; the PDF writer takes raster images, so it is drawn
// to a canvas first. A page that cannot do that prints the name as text.
async function loadWordmark(): Promise<Wordmark | null> {
  try {
    const image = new Image()
    image.src = '/images/muster-logo-light.svg'
    await Promise.race([
      image.decode(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 2000)),
    ])
    const scale = 3
    const canvas = document.createElement('canvas')
    canvas.width = 546 * scale * 0.5
    canvas.height = 78 * scale * 0.5
    const context = canvas.getContext('2d')
    if (!context) return null
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
    return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height }
  } catch {
    return null
  }
}

export function pdfBody(table: ExportTable): string[][] {
  return table.rows.map((row) =>
    row.map((cell, index) => (cell === null ? 'None' : table.columns[index].percent ? `${cell}%` : String(cell))),
  )
}

export function pdfFoot(table: ExportTable): string[][] {
  if (!table.totals || table.rows.length === 0) return []
  const totals = table.totals
  return [
    totals.values.map((value, index) =>
      index === 0 ? totals.label : value === null ? '' : table.columns[index].percent ? `${value}%` : String(value),
    ),
  ]
}

function alignedCell(content: string, column: ExportColumn) {
  return column.numeric && !column.leftAlign ? { content, styles: { halign: 'right' as const } } : content
}

export async function buildPdf(table: ExportTable, wordmark: Wordmark | null) {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')])
  const document = new jsPDF({ orientation: 'landscape', unit: 'pt', format: table.compact ? 'legal' : 'letter' })
  const margin = 36
  let top = margin

  if (wordmark) {
    const height = 20
    document.addImage(wordmark.dataUrl, 'PNG', margin, top, (wordmark.width / wordmark.height) * height, height)
    top += height + 10
  } else {
    document.setFont('helvetica', 'bold').setFontSize(14).text(PRODUCT_NAME, margin, top + 12)
    top += 26
  }
  document.setFont('helvetica', 'bold').setFontSize(13).text(table.title, margin, top + 10)
  document
    .setFont('helvetica', 'normal')
    .setFontSize(8)
    .text(document.splitTextToSize(parametersLine(table), document.internal.pageSize.getWidth() - margin * 2), margin, top + 24)
  document.text(`Generated: ${generatedLabel(table.generatedAt)}`, margin, top + 24 + 10 * Math.ceil(parametersLine(table).length / 170))

  let tableTop = top + 24 + 10 * Math.ceil(parametersLine(table).length / 170) + 12
  const styles = table.compact ? { fontSize: 6.5, cellPadding: 2 } : { fontSize: 8, cellPadding: 3 }
  if (table.summary) {
    const summary = table.summary
    document.setFont('helvetica', 'bold').setFontSize(10).text(summary.title, margin, tableTop + 8)
    autoTable(document, {
      startY: tableTop + 14,
      head: [summary.columns.map((column) => alignedCell(column.header, column))],
      body: summary.rows.map((row) => row.map((cell) => String(cell ?? ''))),
      foot: [summary.total.map((cell, index) => alignedCell(String(cell ?? ''), summary.columns[index]))],
      margin: { left: margin, right: margin, bottom: margin },
      styles,
      headStyles: { fillColor: [60, 70, 90] },
      footStyles: { fillColor: [235, 238, 243], textColor: 20, fontStyle: 'bold' },
      columnStyles: Object.fromEntries(summary.columns.flatMap((column, index) => (column.numeric ? [[index, { halign: 'right' as const }]] : []))),
    })
    tableTop = ((document as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY ?? tableTop) + 18
  }

  autoTable(document, {
    startY: tableTop,
    // columnStyles reach body cells only, so a numeric column's header and
    // total are aligned with its figures here.
    head: [table.columns.map((column) => alignedCell(column.header, column))],
    body: pdfBody(table),
    foot: pdfFoot(table).map((row) => row.map((content, index) => alignedCell(content, table.columns[index]))),
    margin: { left: margin, right: margin, bottom: margin },
    styles,
    headStyles: { fillColor: [60, 70, 90] },
    footStyles: { fillColor: [235, 238, 243], textColor: 20, fontStyle: 'bold' },
    columnStyles: Object.fromEntries(table.columns.flatMap((column, index) => (column.numeric && !column.leftAlign ? [[index, { halign: 'right' as const }]] : []))),
    didDrawPage: () => {
      document.setFontSize(8).text(`Page ${document.getNumberOfPages()}`, document.internal.pageSize.getWidth() - margin, document.internal.pageSize.getHeight() - 16, { align: 'right' })
    },
  })
  return document
}

export async function exportPdf(table: ExportTable, fileName: string): Promise<void> {
  const document = await buildPdf(table, await loadWordmark())
  document.save(fileName)
}
